import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { PATHS } from '@belong/common';
import FullPageSpinner from 'components/FullPageSpinner/FullPageSpinner';
import { fetchHome } from 'store/redux/homes/actions';
import { fetchResidentLeases } from 'store/redux/renter-accounts/actions';
import { selectBookmarks } from 'store/redux/renter-accounts/selectors';
import { useAppDispatch } from 'store/redux/use-app-dispatch';
import { EmployeeAssignmentTypes } from 'models/enums';
import { EMPLOYEE_TITLES } from 'strings/common.strings';
import { ResidentAccountSummaryItem } from 'api/models';
import Home from 'models/home/Home';
import OldSuccess from 'lease-signing-flow-v2/pages/old-success/old-success';

type RouteParams = {
  homeId: string;
  leaseId: string;
};

export const SignedLeaseSuccessPage = () => {
  // `useAppDispatch` rather than `useDispatch`: `clientMiddleware` resolves a
  // `promise` action to the client's result, so the reads below are of the API
  // payload, not of the action object react-redux's default typing describes.
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { homeId, leaseId } = useParams<RouteParams>();
  const bookmarks = useSelector(selectBookmarks);

  const [home, setHome] = useState<Home>();
  const [lease, setLease] = useState<ResidentAccountSummaryItem>();
  const [employeeAssignment, setEmployeeAssignment] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!homeId || !leaseId) {
        setError(true);
        return;
      }

      try {
        setIsLoading(true);
        setError(false);

        // Fetch home and lease data in parallel using homeId from URL
        const [homeResponse, accountLeases] = await Promise.all([
          dispatch(fetchHome(homeId, false)),
          dispatch(fetchResidentLeases({ includeAllResidents: true })),
        ]);

        console.log('homeResponse', homeResponse);
        console.log('accountLeases', accountLeases);

        if (!homeResponse || !accountLeases) {
          console.error('Missing home or lease data:', {
            homeResponse,
            accountLeases,
          });
          setError(true);
          return;
        }

        // Validate homeResponse has required structure before creating Home instance
        if (
          !homeResponse?.basicInfo ||
          !homeResponse?.propertyInfo ||
          !homeResponse?.address
        ) {
          console.error('Home response missing required fields:', {
            hasBasicInfo: !!homeResponse?.basicInfo,
            hasPropertyInfo: !!homeResponse.propertyInfo,
            hasAddress: !!homeResponse.address,
            homeResponse,
          });
          setError(true);
          return;
        }

        // Try to create Home instance, catch any errors during construction
        try {
          setHome(new Home(homeResponse));
        } catch (homeError) {
          console.error('Error creating Home instance:', homeError, {
            homeResponse,
          });
          setError(true);
          return;
        }

        // Find the specific lease by leaseId instead of using the first one
        const matchingLease = accountLeases.leases.find(
          (lease: ResidentAccountSummaryItem) =>
            lease?.leaseInfo?.basicInfo?.leaseId === leaseId,
        );

        if (!matchingLease) {
          console.error('No lease found matching leaseId:', leaseId, {
            availableLeases: accountLeases.leases,
          });
          setError(true);
          return;
        }

        setLease(matchingLease);

        // Get employee assignment
        const isRenew = (homeResponse.leaseOffered?.renewalNumber ?? 0) > 0;
        const assignmentType = isRenew
          ? EmployeeAssignmentTypes.Concierge
          : EmployeeAssignmentTypes.ResidentGrowth;
        const assignment = isRenew
          ? homeResponse.employees?.concierge
          : homeResponse.employees?.residentGrowth || {};

        setEmployeeAssignment({
          ...assignment,
          // `models/enums` is plain JS, so its members widen to `string` and
          // cannot index `EMPLOYEE_TITLES`. Both branches above are keys of it.
          jobTitle:
            EMPLOYEE_TITLES[assignmentType as keyof typeof EMPLOYEE_TITLES],
        });
      } catch (err) {
        console.error('Error fetching success page data:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch, homeId, leaseId]);

  if (error) {
    history.push(
      `${PATHS.RESIDENTS_ACCOUNT_TOURS_AND_APPS}?error=${encodeURIComponent(
        'There was an error loading the success page. Please try again or contact support.',
      )}`,
    );
  }

  if (isLoading || !home || !lease) {
    return <FullPageSpinner />;
  }

  // This component should not be reached - users should be redirected to old-success or new-success routes
  // Keeping this as a fallback that redirects to old-success
  return (
    <OldSuccess
      home={home}
      lease={lease}
      employee={employeeAssignment}
      homeId={homeId}
      bookmarks={bookmarks}
    />
  );
};
